import React, { Fragment } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Label, Row } from 'reactstrap';
import { invert } from 'lodash';

import { space } from '../../base/utils/miscutils';
import DataStore from '../../base/plumbing/DataStore';
import { modifyPage } from '../../base/plumbing/glrouter';
import { gdi } from './greenTagPageUtils';
import C from '../../C';
import Logo from '../../base/components/Logo';
import { ABC, TagHeaderLine } from './GreenTagWidgets';
import GreenTagExporter, { BulkTagExport } from './GreenTagExporter';
import SearchQuery from '../../base/searchquery';
import PropControl from '../../base/components/PropControl';
import Login from '../../base/youagain';
import DevOnly from '../../base/components/DevOnly';
import ListLoad from '../../base/components/ListLoad';
import KStatus from '../../base/data/KStatus';


/** Compact summary of a green tag for display in ListLoad */
function TagListItem({ item: tag }) {
	const path = DataStore.getValue(['location', 'path']);
	const focusTagId = path[1];

	const focused = tag.id === focusTagId;
	let containerProps = {};
	if (!focused) {
		containerProps.role = 'button';
		containerProps.onClick = () => modifyPage(['green', tag.id]);
	}

	// Fetch advertiser so we can display a logo
	const advertiser = tag.vertiser && gdi(C.TYPES.Advertiser, tag.vertiser).value;

	return (
		<Card className={space('inner', focused && 'focused')}>
			<CardHeader className="p-2">
				<div style={{width: '95%', overflow: 'hidden'}} className="d-flex flex-row justify-content-between">
					<div style={{flexGrow: 1}}>
						<TagHeaderLine tag={tag} />
					</div>
				</div>
			</CardHeader>
			<CardBody className="p-2">
				<Logo className="pull-left mr-3" item={advertiser} />
				<div className="d-flex flex-direction-row justify-content-between">
					<ABC type={C.TYPES.Advertiser} tag={tag} noLink noShare />
					<ABC type={C.TYPES.Campaign} tag={tag} noLink noShare />
					<GreenTagExporter tag={tag} />
				</div>
			</CardBody>
		</Card>
	);
}


/** What item-type do we give the PropControlDataItem when selecting which entity to filter on? */
const typeForFilter = {
	agency: C.TYPES.Agency,
	brand: C.TYPES.Advertiser,
	campaign: C.TYPES.Campaign
};
const filterForType = invert(typeForFilter);


/** What GreenTag field name do we put in a SearchQuery for a particular filter-type? */
const tagFieldForFilter = {
	agency: 'agencyId',
	brand: 'vertiser',
	campaign: 'campaign'
};
const filterForTagField = invert(tagFieldForFilter);


/**
 * What items does/can each item type have? eg an Advertiser has an Agency, a Campaign has an Agency and and Advertiser etc.
 * TODO Generalise this & put "has-a" information in the JS file of each data type?
 */
const filterableFieldsForType = {
	[C.TYPES.Agency]: { },
	[C.TYPES.Advertiser]: { [C.TYPES.Agency]: 'agencyId' },
	[C.TYPES.Campaign]: { [C.TYPES.Agency]: 'agencyId', [C.TYPES.Advertiser]: 'vertiser' },
	[C.TYPES.GreenTag]: { [C.TYPES.Agency]: 'agencyId', [C.TYPES.Advertiser]: 'vertiser', [C.TYPES.Campaign]: 'campaign' }
};


/**
 * PropControlDataItem for filtering by agency, brand, campaign.
 * Reads URL values for existing filters & uses them to pre-filter the dropdown
 * -- e.g. if there's a filter-by-agency in place, only brands belonging to that agency will show.
 */
function ABCFilterControl({itemType}) {
	let sq = null;
	const typeToQParam = filterableFieldsForType[itemType];
	Object.entries(typeToQParam).forEach(([type, qParam]) => {
		const filterFromURL = DataStore.getUrlValue(filterForType[type]);
		if (filterFromURL) sq = SearchQuery.setProp(sq, qParam, filterFromURL);
	});

	return <PropControl type="DataItem" q={sq?.query} itemType={itemType} label prop={filterForType[itemType]} />;
}


/**
 * The right pane, where tags are listed and can be bulk-exported.
 */
function GreenTagList() {
	// Filter by agency/brand/campaign
	let sq = null;
	Object.entries(tagFieldForFilter).forEach(([urlParam, tagField]) => {
		// Add "agencyId:xxxx", "vertiser:xxxx" etc clauses to tag list query
		const filterVal = DataStore.getUrlValue(urlParam);
		if (filterVal) sq = SearchQuery.setProp(sq, tagField, filterVal);
	});
	// Only show tags with unweighed creatives?
	if (DataStore.getUrlValue('unweighed')) {
		sq = SearchQuery.setProp(sq, 'weight', 'unset');
	}
	const q = sq && sq.query;
	const filter = DataStore.getUrlValue('filter');

	return <>
		<h4>{Login.getUser().name}</h4>
			<h3>Green Ad Tag List</h3>
			<Form className="border p-1 form-compact">
				<Container>
					<h4>Filter</h4>
					<Row>
						{Object.values(typeForFilter).map(itemType => <Col key={itemType} xs="4">
							<ABCFilterControl itemType={itemType} />
						</Col>)}
					</Row>
					<Row>
						<Col>
							<PropControl type="search" label="Keyword" prop="filter" />
						</Col>
						<Col>
							<FormGroup>
								<Label>Export current list</Label>
								<BulkTagExport q={q} filter={filter} />
							</FormGroup>
						</Col>
						<Col>
							<DevOnly test>
								<PropControl type="checkbox" prop="unweighed" label="Unweighed only" help="If ticked, only list tags with unweighed creatives. Otherwise list them all." />
							</DevOnly>
						</Col>
					</Row>
				</Container>
			</Form>
			<ListLoad otherParams={{ filterByShares: true }}
				q={q}
				filter={filter}
				filterLocally
				type={C.TYPES.GreenTag}
				status={KStatus.PUBLISHED}
				ListItem={TagListItem} itemClassName="GreenTagItem w-100"
				notALink
				pageSize={20}
				pageSelectID="TagsPage"
			/>
	</>;
}


export default GreenTagList;
